import { useTranslation, type TFunction } from 'react-i18next';

interface BusinessItemProps {
  icon: string;
  en: string;
  ja: string;
  km: string;
  index: number;
}

const BusinessItem = ({ icon, en, ja, km, index }: BusinessItemProps) => {
  return (
    <div
      className="bg-white rounded-sm p-5 md:p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-500 group border border-transparent hover:border-gold-200/60"
      itemScope
      itemType="https://schema.org/Service"
    >
      <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-sm bg-navy-50 mb-4 group-hover:bg-navy-800 transition-colors duration-500">
        <i className={`${icon} text-lg md:text-xl text-gold-500 group-hover:text-gold-300 transition-colors duration-500`}></i>
      </div>
      <div className="mb-3">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-gold-300/50 text-xs font-semibold text-gold-600 bg-gold-50">
          {index}
        </span>
      </div>
      <div className="space-y-3">
        <div>
          <p itemProp="name" className="text-sm md:text-base font-semibold text-navy-900 leading-snug">{en}</p>
        </div>
        <div className="border-t border-gray-100 pt-2">
          <p className="text-sm text-gray-600 leading-snug font-jp">{ja}</p>
        </div>
        <div className="border-t border-gray-100 pt-2">
          <p className="text-sm text-gray-600 leading-snug font-km">{km}</p>
        </div>
      </div>
    </div>
  );
};

const Business = () => {
  const { t }: { t: TFunction } = useTranslation();

  const businessItems: BusinessItemProps[] = [
    {
      icon: 'ri-user-search-line',
      en: t('business_6_en'),
      ja: t('business_6_ja'),
      km: t('business_6_km'),
      index: 1,
    },
    {
      icon: 'ri-building-line',
      en: t('business_3_en'),
      ja: t('business_3_ja'),
      km: t('business_3_km'),
      index: 2,
    },
    {
      icon: 'ri-hotel-line',
      en: t('business_4_en'),
      ja: t('business_4_ja'),
      km: t('business_4_km'),
      index: 3,
    },
    {
      icon: 'ri-restaurant-line',
      en: t('business_1_en'),
      ja: t('business_1_ja'),
      km: t('business_1_km'),
      index: 4,
    },
    {
      icon: 'ri-store-2-line',
      en: t('business_2_en'),
      ja: t('business_2_ja'),
      km: t('business_2_km'),
      index: 5,
    },
    {
      icon: 'ri-recycle-line',
      en: t('business_5_en'),
      ja: t('business_5_ja'),
      km: t('business_5_km'),
      index: 6,
    },
    {
      icon: 'ri-global-line',
      en: t('business_7_en'),
      ja: t('business_7_ja'),
      km: t('business_7_km'),
      index: 7,
    },
  ];

  return (
    <section id="business" className="bg-navy-950 py-20 md:py-28" aria-labelledby="business-heading">
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
        <div className="text-center mb-12 md:mb-20">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-8 h-px bg-gold-400" aria-hidden="true"></span>
            <p className="text-gold-400 text-xs md:text-sm font-semibold tracking-[0.25em] uppercase">
              {t('business_label')}
            </p>
            <span className="w-8 h-px bg-gold-400" aria-hidden="true"></span>
          </div>
          <h2 id="business-heading" className="text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-white leading-tight mb-5">
            {t('business_label')}
          </h2>
          <p className="text-sm md:text-base text-gray-400 max-w-2xl mx-auto leading-relaxed">
            {t('business_desc')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {businessItems.map((item) => (
            <BusinessItem key={item.index} {...item} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Business;
